/**
 * BetaBadge — the word BETA set as a SUPERSCRIPT to the DECO wordmark, and the
 * explainer modal it opens.
 *
 * ── Design history, because it has changed three times ───────────────────
 * 1. Full-width light-blue pill under the wordmark → founder: "too bulky".
 * 2. Circular test-tube badge beside the wordmark  → founder: "I kind of hate
 *    our circular test tube icon".
 * 3. Plain text UNDER the wordmark → founder: "the beta still looks horrible".
 * 4. (current) SUPERSCRIPT to the wordmark — raised alongside the mark, the
 *    way a trademark symbol sits, rather than stacked beneath it.
 *
 * Throughout, the typography is the wordmark's own (weight 200, italic,
 * Helvetica Neue, tracking proportional to size) at roughly a third of its
 * scale, in --text-secondary. It reads as part of the mark rather than as a
 * badge stuck onto it.
 *
 * ── Still clickable, deliberately ────────────────────────────────────────
 * The founder asked to remove the circular ICON, not the explainer behind it —
 * that modal was specced in the same breath as "explain the bug logging". So
 * the text stays a real <button> with its own accessible name and no visual
 * chrome. If a purely static label is wanted, dropping the button wrapper is a
 * one-line change; flagged rather than assumed.
 *
 * ON THE BUG-REPORTING COPY. Points at the DEC-1148 Feedback widget, which is
 * the real in-product reporter (it attaches the page and recent scrubbed
 * errors, so most reports need no reproduction round-trip). Email is kept as a
 * secondary option rather than the primary instruction.
 */

import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useModalFocusTrap } from '../hooks/useModalFocusTrap';

const SUPPORT_EMAIL = 'deco@decoenergy.net';

// Tracking is fontSize / 3 — the same ratio DecoWordmark's navbar preset uses
// (12px at 36px). Holding the RATIO rather than a pixel value keeps this in
// the brand's typographic voice at any scale.
const LETTER_SPACING_DIVISOR = 3;

// "Much smaller than the wordmark": the navbar mark is 36px desktop / 28px
// mobile, so these are roughly a third — clearly subordinate, still
// unmistakably the same lettering.
const SIZE_PRESETS = {
  navbar: { fontSize: '11px' },
  'navbar-mobile': { fontSize: '9px' },
};

export const BetaExplainerModal = ({ onClose }) => {
  const dialogRef = useRef(null);
  const trap = useModalFocusTrap({ active: true, containerRef: dialogRef, onClose });

  return ReactDOM.createPortal(
    <div className="modal" onKeyDown={trap.onKeyDown}>
      <div
        className="modal-content"
        style={{ maxWidth: '520px' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="beta-explainer-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="modal-header">
          <h2 id="beta-explainer-title">DECO is in beta</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--text-primary)' }}>
          <p style={{ marginTop: 0 }}>
            You’re using DECO before its general release. Everything here is real — your
            sites, your enrollments, and your earnings are live, not sample data — but
            you’re seeing features while they’re still being built and refined.
          </p>

          <p>
            That means you should expect the occasional rough edge: a screen that looks
            unfinished, a number that takes a moment to catch up, or a step that isn’t as
            obvious as it should be.
          </p>

          <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)', marginBottom: '6px' }}>
            Found a bug? Tell us.
          </h3>
          <p style={{ marginTop: 0 }}>
            Use the <strong>Beta Feedback</strong> button — the blue chat bubble marked
            BETA in the bottom-right corner of any
            page. It attaches what page you were on and any recent errors, so we can
            usually find the problem without asking you to reproduce it. The more you
            can tell us, the faster we can fix it:
          </p>
          <ul style={{ margin: '0 0 16px', paddingLeft: '20px' }}>
            <li>what you were trying to do</li>
            <li>what happened instead</li>
            <li>the page you were on (the web address helps)</li>
            <li>a screenshot, if you have one</li>
          </ul>

          <p style={{ marginBottom: 0, color: 'var(--text-secondary)', fontSize: '13px' }}>
            Reporting something that turns out not to be a bug is never a waste of our
            time — if a screen confused you, that’s worth knowing too. You can also
            email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=DECO%20beta%20feedback`} style={{ color: 'var(--navy)' }}>
              {SUPPORT_EMAIL}
            </a>{' '}
            if you’d rather.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const BetaBadge = ({ size = 'navbar', style: styleOverride = {} }) => {
  const [open, setOpen] = useState(false);
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.navbar;
  const fontSizePx = parseFloat(preset.fontSize);
  const letterSpacingPx = Math.max(2, Math.round(fontSizePx / LETTER_SPACING_DIVISOR));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="DECO is in beta — what this means"
        style={{
          // Superscript: an inline-block raised against the wordmark's
          // baseline. alignSelf handles the raise in the navbar's flex row;
          // this keeps the element itself simple.
          display: 'inline-block',
          // No pill, no fill, no border, no icon — every chrome treatment so
          // far was rejected. This is type, not chrome.
          background: 'none',
          border: 0,
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          // The wordmark's own typography, at a third the scale.
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
          fontWeight: 200,
          fontStyle: 'italic',
          // This IS a <button>, so the 2026-08-09 condensed-display rule
          // applies directly to it; font-stretch must be reset or the mark
          // renders in Helvetica Neue's condensed BOLD face.
          fontStretch: 'normal',
          fontSize: preset.fontSize,
          letterSpacing: `${letterSpacingPx}px`,
          color: 'var(--text-secondary)',
          lineHeight: 1,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          ...styleOverride,
        }}
      >
        BETA
      </button>
      {open && <BetaExplainerModal onClose={() => setOpen(false)} />}
    </>
  );
};

export default BetaBadge;
